import { useState, useMemo, useCallback, useEffect } from 'react';
import FretebrasLogo from 'public/assets/favicon.svg';

type NotificationProps = {
  title: string;
  options?: NotificationOptions;
  onClick?: (event: Event) => void;
};

const useBrowserNotification = () => {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);

  const hasPermission = useMemo(() => permission === 'granted', [permission]);

  const checkNotificationPermission = () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  };

  const requestPermission = async () => {
    if (!permission || permission !== 'default') return;
    const newPermission = await Notification.requestPermission();
    setPermission(newPermission);
  };

  const notify = useCallback(
    ({ title, options, onClick }: NotificationProps) => {
      if (!hasPermission || !permission) return;

      const defaultOptions: NotificationOptions = {
        icon: FretebrasLogo,
        ...options,
      };

      const notification = new Notification(title, defaultOptions);

      if (onClick) {
        notification.onclick = onClick;
      }
    },
    [hasPermission, permission]
  );

  const dispatchNotification = useCallback(
    (props: NotificationProps) => {
      if (!hasPermission || !permission) return;
      notify(props);
    },
    [hasPermission, notify, permission]
  );

  useEffect(() => {
    checkNotificationPermission();
    document.addEventListener('visibilitychange', checkNotificationPermission);

    return () => {
      document.removeEventListener('visibilitychange', checkNotificationPermission);
    };
  }, []);

  return { hasPermission, requestPermission, permission, dispatchNotification };
};

export default useBrowserNotification;
